import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, ShieldCheck, ChevronDown } from "lucide-react";
import Emblem from "./Emblem";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { useLang } from "../i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";

const LINKS = [
  { to: "/about", label: "About", children: [{ to: "/about", label: "About Us" }, { to: "/about/leadership", label: "Leadership" }] },
  { to: "/chapters", label: "Chapters" },
  { to: "/membership", label: "Membership" },
  { to: "/events", label: "Events" },
  { to: "/welfare", label: "Welfare" },
  { to: "/gallery", label: "Gallery" },
  { to: "/newsletter", label: "Newsletter" },
  { to: "/contact", label: "Contact", children: [{ to: "/contact", label: "Contact Us" }, { to: "/contact/faq", label: "FAQ" }, { to: "/contact/connect", label: "Connect with us" }] },
];

const subId = (label) => label.toLowerCase().replace(/\s+/g, "-");

function DesktopNav() {
  const loc = useLocation();
  const { t } = useLang();
  return (
    <nav className="hidden items-center gap-6 lg:flex">
      {LINKS.map((l) => {
        const active = loc.pathname === l.to || loc.pathname.startsWith(`${l.to}/`);
        const cls = `text-sm font-medium transition-colors hover:text-crimson ${active ? "text-crimson" : "text-foreground/80"}`;
        if (!l.children) return <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`} className={cls}>{t(l.label)}</Link>;
        return (
          <div key={l.to} className="group relative" data-testid={`nav-${l.label.toLowerCase()}-menu`}>
            <Link to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`} className={`${cls} inline-flex items-center gap-1`}>{t(l.label)} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" /></Link>
            <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 opacity-0 transition-all group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="glass min-w-[200px] rounded-xl border border-gold/30 p-2 shadow-xl">
                {l.children.map((c) => (
                  <Link key={c.to} to={c.to} data-testid={`nav-sub-${subId(c.label)}`}
                    className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-gold-light hover:text-crimson ${loc.pathname === c.to ? "bg-gold-light text-crimson" : "text-foreground/80"}`}>{t(c.label)}</Link>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

function AuthButtons() {
  const { user, isOfficer } = useAuth();
  const nav = useNavigate();
  const { t } = useLang();
  if (user) {
    return (
      <>
        {isOfficer && <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="nav-admin-btn" className="gap-1 text-palm"><ShieldCheck size={16} /> {t("Admin")}</Button>}
        <Button size="sm" onClick={() => nav("/portal")} data-testid="nav-portal-btn" className="gap-1 bg-crimson hover:bg-crimson-hover"><LayoutDashboard size={16} /> {t("My Portal")}</Button>
      </>
    );
  }
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="nav-login-btn">{t("Sign In")}</Button>
      <Button size="sm" onClick={() => nav("/portal/dues")} data-testid="nav-paydues-btn" className="bg-gold text-crimson hover:bg-gold/90 font-semibold">{t("Pay Dues")}</Button>
    </>
  );
}

function MobileMenu({ onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const { t } = useLang();
  return (
    <div className="border-t border-gold/20 bg-parchment-card px-4 py-4 lg:hidden" data-testid="mobile-menu">
      <div className="flex flex-col gap-1">
        {LINKS.map((l) => (
          <div key={l.to}>
            <Link to={l.to} onClick={onClose} className="block rounded-lg px-2 py-2.5 text-base font-medium active:bg-gold-light" data-testid={`m-nav-${l.label.toLowerCase()}`}>{t(l.label)}</Link>
            {l.children && (
              <div className="ml-4 flex flex-col border-l-2 border-gold/40 pl-3">
                {l.children.map((c) => <Link key={c.to} to={c.to} onClick={onClose} className="block rounded-lg px-2 py-2.5 text-sm text-foreground/75 active:bg-gold-light" data-testid={`m-nav-sub-${subId(c.label)}`}>{t(c.label)}</Link>)}
              </div>
            )}
          </div>
        ))}
        <div className="mt-2 px-2"><LanguageToggle /></div>
        <div className="mt-3 flex gap-2 px-2">
          {user ? (
            <Button onClick={() => nav("/portal")} className="h-11 flex-1 bg-crimson">{t("My Portal")}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => nav("/login")} className="h-11 flex-1">{t("Sign In")}</Button>
              <Button onClick={() => nav("/portal/dues")} className="h-11 flex-1 bg-gold text-crimson">{t("Pay Dues")}</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  return (
    <header className="glass sticky top-0 z-50 w-full" data-testid="public-navbar">
      <div className="kente-border" />
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-link">
          <Emblem size={44} />
          <div className="leading-tight">
            <div className="font-serif-display text-base font-bold leading-tight text-crimson">Okuapemman Association</div>
            <div className="eyebrow text-gold">of {t("Massachusetts")}</div>
          </div>
        </Link>
        <DesktopNav />
        <div className="hidden items-center gap-2 lg:flex"><LanguageToggle /><AuthButtons /></div>
        <button className="flex h-11 w-11 items-center justify-center rounded-lg lg:hidden" aria-label="Toggle menu" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
}
